import { createFileRoute, Link } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useTranslation } from 'react-i18next'
import { useLang } from '@/hooks/useLang'
import { Globe, ArrowRight, Phone, CheckCircle2, Clock } from 'lucide-react'
import { generateSeo } from '@/lib/seo'
import { Breadcrumbs } from '@/components/Breadcrumbs'
import { ToolPageHero } from '@/components/ToolPageHero'

export const Route = createFileRoute('/$lang/tools/')({
  staticData: {
    sitemap: { priority: '0.6', changefreq: 'monthly' },
  },
  component: ToolsIndexPage,
  head: ({ match }) => {
    const { meta, links } = generateSeo({
      title: 'Free International Calling Tools | PhoneSalad',
      description: 'Free utilities to help you make international calls. Look up country dialling codes, validate phone numbers, and find the best time to call any country.',
      pathname: match.pathname,
    })
    return { meta, links }
  },
})

function ToolsIndexPage() {
  const { t } = useTranslation(['tools', 'common'])
  const lang = useLang()

  const tools = [
    {
      id: 'country-codes',
      title: t('tools.countryCodes.title'),
      description: t('tools.countryCodes.description'),
      icon: Globe,
      href: '/$lang/tools/country-codes',
    },
    {
      id: 'phone-validator',
      title: t('tools.phoneValidator.title'),
      description: t('tools.phoneValidator.description'),
      icon: CheckCircle2,
      href: '/$lang/tools/phone-validator',
    },
    {
      id: 'best-time-to-call',
      title: t('tools.bestTimeToCall.title'),
      description: t('tools.bestTimeToCall.description'),
      icon: Clock,
      href: '/$lang/tools/best-time-to-call',
    },
  ]

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('tools.breadcrumb'),
            to: '/$lang/tools',
          },
        ]}
      />

      <ToolPageHero
        icon={Phone}
        badgeText={t('tools.title')}
        title={t('tools.index.title')}
        description={t('tools.index.description')}
      />

      {/* Tools List */}
      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold mb-6 text-center">
            {t('tools.index.toolsList')}
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {tools.map((tool) => {
              const Icon = tool.icon
              return (
                <Link
                  key={tool.id}
                  to={tool.href}
                  params={{ lang }}
                  className="block group"
                >
                  <Card className="h-full hover:shadow-lg transition-shadow group-hover:border-primary/50">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{tool.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-grow">
                      <p className="text-muted-foreground text-sm flex-grow">
                        {tool.description}
                      </p>
                      <div className="mt-4 pt-4 border-t flex items-center justify-between">
                        <span className="text-sm font-medium text-primary">
                          {t('common.tryNow')}
                        </span>
                        <ArrowRight className="w-4 h-4 text-primary transition-transform group-hover:translate-x-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              )
            })}
          </div>
        </div>
      </section>
    </>
  )
}
